'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { PanelIndexContext, useAppShell } from './AppShellContext';

export interface AppShellContentProps extends React.ComponentProps<'div'> {}

/**
 * Card wrapper providing Figma-designed visual treatment for panel content.
 * Solid background, shadow-sm elevation, rounded corners, subtle border.
 *
 * In connected mode, reads AppShellContext + PanelIndexContext to adapt
 * border-radius based on panel position (first/last/only).
 */
function AppShellContent({
  className,
  children,
  ...props
}: AppShellContentProps) {
  const context = useAppShell();
  const connected = context?.connected ?? false;

  // Read panel index from PanelIndexContext (set by Root via provider)
  const panelIndex = React.useContext(PanelIndexContext);
  const position =
    connected && context ? context.getPanelPosition(panelIndex) : null;

  return (
    <div
      data-slot="app-shell-content"
      className={cn(
        // Core layout
        'flex flex-col h-full min-h-0 overflow-clip',
        // Figma card styles
        'bg-background dark:bg-surface-1',
        'border border-border',
        'shadow-sm',
        // Border radius: connected mode adapts per position
        !connected && 'rounded-2xl',
        connected && position === 'only' && 'rounded-2xl',
        connected &&
          position === 'first' && [
            'rounded-ss-2xl rounded-es-2xl',
            'rounded-se-none rounded-ee-none',
          ],
        connected &&
          position === 'last' && [
            'rounded-ss-none rounded-es-none',
            'rounded-se-2xl rounded-ee-2xl',
          ],
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

AppShellContent.displayName = 'AppShell.Content';

export { AppShellContent };
